import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Breadcrumb, Filters, List, Page, Row, SearchInput, SpellListItem } from '../../components';
import { db } from '../../firebase';
import { addDoc, collection, doc, getDoc, updateDoc } from 'firebase/firestore';
import { Button, TextField } from '@mui/material';
import './EditListPage.scss';
import { SPELLS } from '../../data';
import { applyAllFilters, getSpellId } from '../../util/spell';
import { AuthContext, SettingsContext, ToastContext } from '../../context';

export const EditListPage = () => {
    const { listId } = useParams();
    const [ searchParams ] = useSearchParams();
    const spellId = searchParams.get('spellId');
    const [ list, setList ] = useState(null);
    const [ initialSpells, setInitialSpells ] = useState([]);
    const [ searchString, setSearchString ] = useState('');
    const [ filters, setFilters ] = useState({});
    const settings = useContext(SettingsContext);
    const { toast } = useContext(ToastContext);
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();

    useEffect(() => {
        if(listId) {
            const docRef = doc(db, "spellLists", listId);

            getDoc(docRef).then(docSnap => {
                const data = docSnap.data();
                setList(data);
                setInitialSpells(data.spells);
            });
        } else {
            setList({
                name: 'Unnamed',
                spells: !!spellId ? [spellId] : []
            });
            if (!!spellId) {
                setInitialSpells([spellId]);
            }
        }
    }, [listId, spellId]);

    const updateList = () => {
        const docRef = doc(db, 'spellLists', listId);

        return updateDoc(docRef, list);
    };

    const createList = () => {
        return addDoc(collection(db, 'spellLists'), {
            ...list,
            createdAt: new Date(),
            userId: user.uid
        });
    };

    const spellAdded = (spell) => (
        list?.spells.includes(getSpellId(spell))
    );

    const handleNameChange = e => {
        setList({
            ...list,
            name: e.target.value
        });
    };
    
    const handleAddSpell = (spell) => {
        setList({
            ...list,
            spells: [...new Set([
                ...list.spells,
                getSpellId(spell)
            ])]
        });
    };
    const handleRemoveSpell = (spell) => {
        setList({
            ...list,
            spells: list.spells.filter(s => s !== getSpellId(spell))
        });
    };

    const handleSave = () => {
        if (listId) {
            updateList()
                .then(() => {
                    toast('Save successful');
                    navigate(-1);
                })
                .catch((e) => {
                    console.error(e);
                    toast('Something went wrong');
                });
        } else {
            createList()
                .then(() => {
                    toast('List created');
                    navigate(-1);
                })
                .catch((e) => {
                    console.error(e);
                    toast('Something went wrong');
                });
        }
    };

    const compareSpells = (s1, s2) => {
        const hadS1 = initialSpells.includes(getSpellId(s1));
        const hadS2 = initialSpells.includes(getSpellId(s2));
        if (hadS1 !== hadS2) {
            return hadS1 ? -1 : 1;
        }
        if (s1.level === s2.level) {
            return s1.name.localeCompare(s2.name);
        }
    
        return s1.level - s2.level;
    };

    const filteredSpells = () => {
        const regex = new RegExp(searchString.toLowerCase());

        return SPELLS
            .filter(({ name }) => regex.test(name.toLowerCase()))
            .filter(s => applyAllFilters(s, filters, settings))
            .sort(compareSpells);
    };

    return (
        <Page className="EditListPage">
            <Breadcrumb />
            <div className='pageHeader'>
                <Row>
                    <h2>
                        <TextField variant='standard' color='secondary' value={list?.name || ''} onChange={handleNameChange}/>
                        's list
                    </h2>
                </Row>
                <SearchInput value={searchString} onChange={setSearchString} />
            </div>
            <Filters filters={filters} onChange={setFilters} />

            <List>
                {filteredSpells().map((s, i) => (
                    <SpellListItem 
                        key={i}
                        spell={s}
                        addable
                        added={spellAdded(s)}
                        onAdd={() => handleAddSpell(s)}
                        onRemove={() => handleRemoveSpell(s)}
                    />
                ))}
            </List>

             <div className='EditListPage-Footer'>
                <Button variant='contained' onClick={handleSave}>Save</Button>
             </div>
        </Page>
    );
};